import { Loader } from 'components';
import { PlatformRoleEnum, SiteRoleEnum } from 'domains/roleDomain';
import CompanyGallery from 'features/CompanyGallery';
import Gallery from 'features/Gallery';
import Login from 'features/Login';
import EmailOtpLogin from 'features/Login/components/EmailOtpLogin';
import SsoCallback from 'features/Login/components/SsoCallback';
import QuickbooksCallback from 'features/Login/components/QuickbooksCallback';
import { NotFound } from 'features/NotFound';
import OrderHistory from 'features/OrderHistory';
import Platform from 'features/Platform';
import Unavailable from 'features/Unavailable';
import { usePage, useUser } from 'hooks';
import { keyBy } from 'lodash-es';
import { LazyExoticComponent, Suspense, lazy, useMemo } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { SYSTEM_UNAVAILABLE_STATUS_KEY } from 'utils/constants';
import { isSourcer } from 'utils/rbac';
import JobArchive from 'features/JobArchive';

// Dynamic loading modules
const ShoppingCart = lazy(
  () => import(/* webpackChunkName: "shoppingcart" */ 'features/ShoppingCart')
);
const Workspace = lazy(
  () => import(/* webpackChunkName: "workspace" */ 'features/Workspace')
);
const Projects = lazy(
  () => import(/* webpackChunkName: "projects" */ 'features/Projects')
);
// const OrderHistory = lazy(
//   () => import(/* webpackChunkName: "orderHistory" */ 'features/OrderHistory')
// );
const JobConfig = lazy(
  () => import(/* webpackChunkName: "jobConfig" */ 'features/JobConfig')
);
const Notification = lazy(
  () => import(/* webpackChunkName: "notification" */ 'features/Notification')
);
const SsoBuyerShop = lazy(
  () => import(/* webpackChunkName: "ssoBuyerShop" */ 'features/SsoBuyerShop')
);
const SsoBuyerCompanyGallery = lazy(
  () =>
    import(
      /* webpackChunkName: "ssoBuyerCompanyGallery" */ 'features/SsoBuyerCompanyGallery'
    )
);

export interface IRoute {
  name?: string;
  path: string;
  component: (() => JSX.Element) | LazyExoticComponent<() => JSX.Element>;
  hide?: boolean;
}

const UNAVAILABLE_PATH = '/unavailable';

const Router = () => {
  const defaultRoute = routes.find((route) => route.name === 'gallery');
  const adminDefaultRoute = '/platform/org';
  const loginPath = routes.find((route) => route.name === 'login')?.path;
  const user = useUser();
  const { isLoginPage, isPlatformPage, isRootAddress } = usePage();
  const { search } = useLocation();
  const token = new URLSearchParams(search).get('token');
  const redirectTo = new URLSearchParams(search).get('redirect');
  const isUnavailableStatus = !!JSON.parse(
    localStorage.getItem(SYSTEM_UNAVAILABLE_STATUS_KEY) || 'false'
  );
  const isNoAdminAccess = useMemo(() => {
    return (
      isPlatformPage && user?.platformRole === PlatformRoleEnum.NoAdminAccess
    );
  }, [user?.platformRole, isPlatformPage]);

  const isNoSiteAccess = useMemo(() => {
    return (
      !isLoginPage &&
      !isPlatformPage &&
      user?.siteRole === SiteRoleEnum.NoSiteAccess
    );
  }, [isLoginPage, user?.siteRole, isPlatformPage]);

  const isAdmin = useMemo(() => {
    return user?.platformRole !== PlatformRoleEnum.NoAdminAccess;
  }, [user]);

  return (
    <Suspense fallback={<Loader loading />}>
      <Switch>
        <Route path={UNAVAILABLE_PATH} component={Unavailable} />
        {isUnavailableStatus && (
          <>
            <Redirect to={UNAVAILABLE_PATH} />
          </>
        )}
        {!token && isLoginPage && user && redirectTo && (
          <Redirect to={redirectTo ? `/${redirectTo}` : '/'} />
        )}
        <Route path="/auth/callback" component={SsoCallback} />
        <Route path="/quickbooks/callback" component={QuickbooksCallback} />
        <Route path="/login/otp" component={EmailOtpLogin} />
        <Route path={loginPath} component={Login} />
        {/* exact: otherwise path "/" matches every pathname and unauthenticated users
            never reach /job-config/...?canvaExchange= (Canva publish "Check it out"). */}
        {!user && (
          <Redirect exact from="/" to={loginPath as string} />
        )}
        {Boolean((isNoAdminAccess || isNoSiteAccess) && !isRootAddress) && (
          <Route component={NotFound} />
        )}
        {routes
          .filter((route) => !route.hide)
          .map((route) => (
            <Route
              key={route.path}
              path={route.path}
              component={route.component}
            />
          ))}
        {/*{isAdmin && <Redirect from="/" to={adminDefaultRoute} />}*/}
        {isSourcer(user) && <Redirect from="/" to={'gallery'} />}
        <Redirect
          exact
          from="/"
          to={{
            pathname: isAdmin
              ? adminDefaultRoute
              : isSourcer(user)
              ? 'workspace'
              : defaultRoute?.path,
          }}
        />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
};

export default Router;

/**
 * login route will be used in usePage hook
 */
export const routes: IRoute[] = [
  {
    name: 'login',
    path: '/login',
    component: Login,
    hide: true,
  },
  {
    name: 'platform',
    path: '/platform',
    component: Platform,
  },
  {
    name: 'gallery',
    path: '/gallery',
    component: Gallery,
  },
  {
    name: 'ssoBuyerShop',
    path: '/sso-buyer-shop',
    component: SsoBuyerShop,
  },
  {
    name: 'ssoBuyerCompanyGallery',
    path: '/sso-buyer-company-gallery/:id',
    component: SsoBuyerCompanyGallery,
  },
  {
    name: 'companyGallery',
    path: '/company-gallery',
    component: CompanyGallery,
  },
  {
    name: 'shoppingCart',
    path: '/shopping-cart',
    component: ShoppingCart,
  },
  {
    name: 'workspace',
    path: '/workspace',
    component: Workspace,
  },
  {
    name: 'projects',
    path: '/projects',
    component: Projects,
  },
  {
    name: 'orderHistory',
    path: '/order-history',
    component: OrderHistory,
  },
  {
    name: 'jobConfig',
    path: '/job-config',
    component: JobConfig,
  },
  {
    name: 'notification',
    path: '/notification',
    component: Notification,
  },
  {
    name: 'unavailable',
    path: '/unavailable',
    component: Unavailable,
  },
  {
    name: 'jobArchive',
    path: '/job-archive',
    component: JobArchive,
  },
];

interface RoutesMap {
  [key: string]: IRoute;
  workspace: IRoute;
  projects: IRoute;
  orderHistory: IRoute;
  jobConfig: IRoute;
  notification: IRoute;
  unavailable: IRoute;
  shoppingCart: IRoute;
  gallery: IRoute;
  ssoBuyerShop: IRoute;
  ssoBuyerCompanyGallery: IRoute;
  companyGallery: IRoute;
  login: IRoute;
  platform: IRoute;
  jobArchive: IRoute;
}

export const routesMap = keyBy(routes, 'name') as RoutesMap;
